<template>
  <div class="img-slide-parts">
    <router-link
      class="img-slide-parts__slide img-slide-parts__router"
      v-if="linkData.linkType === 'router'"
      :to="linkData.link"
    >
      <img class="img-slide-parts__img" :src="thumbnailPath" alt="" />
      <div class="img-slide-parts__content">
        <p class="img-slide-parts__text">
          {{ linkData.text }}
        </p>
        <p class="img-slide-parts__button-wrapper">
          <span
            class="img-slide-parts__button"
            :class="{ 'img-slide-parts__button--circuit-mode': isCircuitMode }"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 3L14.2854 8.57125C14.6091 8.76546 14.6091 9.23454 14.2854 9.42875L5 15V3Z"
                fill="#00ffba"
              />
            </svg>

            <span>{{ $tc('HomePage.playButton') }}</span>
          </span>
        </p>
      </div>
    </router-link>

    <a
      href="javascript:void(0)"
      class="img-slide-parts__slide img-slide-parts__link"
      v-else-if="linkData.linkType === 'link'"
      @click="onClickAnchorLink(linkData)"
    >
      <img class="img-slide-parts__img" :src="thumbnailPath" alt="" @error="onImageError" />
      <div class="img-slide-parts__content">
        <div class="img-slide-parts__text-wrapper">
          <p class="img-slide-parts__text">
            {{ linkData.text }}
            <i><img :src="require('@/assets/img/icon/icon_external_link_gray.svg')" alt="" /></i>
          </p>
        </div>
      </div>
    </a>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from '@vue/composition-api'
import { ImgSliderLinkData } from '@/components/HomePage/HomePane/ImgSliderSection.vue'
import CloudFrontUtil from '@/util/aws/CloudFrontUtil'
import InAppBrowserWrapper, { InAppBrowserResult } from '@/util/inAppBrowser/InAppBrowserWrapper'
import MessageDialogStore from '@/store/stores/pageStore/common/MessageDialogStore'
import I18n from '@/locales/I18n'
import StoreUtil from '@/store/StoreUtil'
import UserStore from '@/store/stores/pageStore/common/UserStore'
import LoginStore from '@/store/stores/loginStore/LoginStore'
import LocalStorageAccessor from '@/util/localstorage/LocalStorageAccessor'

/**
 * HOME イメージスライダー: スライドセクション
 */
export default defineComponent({
  name: 'ImgSlideParts',
  props: {
    linkData: {
      type: Object as PropType<ImgSliderLinkData>,
      required: true,
    },
  },
  setup(props) {
    const appConfigStore = StoreUtil.useStore('AppConfigStore')
    // サムネイル画像のURLをCloudFrontの署名URLに変換する
    const thumbnailPath = CloudFrontUtil.getSignedUrl(props.linkData.img)

    const inAppBrowser = new InAppBrowserWrapper()

    /**
     * ヘッドラインのアンカーリンクがクリックされた場合に呼び出される。
     *
     * @param sliderLink 外部サイトのリンク
     */
    const onClickAnchorLink = (sliderLink: ImgSliderLinkData) => {
      if (!InAppBrowserWrapper.isAvailable()) {
        // ブラウザの場合、別ウィンドウか別タブで表示する
        const subWindow = window.open(sliderLink.link, '_blank')
        if (subWindow && process.env.VUE_APP_SFBINGO_URL === sliderLink.link) {
          // URLがSFbingoの場合には、APIの認証情報とユーザーIDをローカルストレージに保存する
          LocalStorageAccessor.setCredentialInfo(
            UserStore.value.user.value._organization || '',
            UserStore.value.user.value.id || '',
            LoginStore.value.accessToken || '',
            LoginStore.value.refreshToken || '',
          )
          setTimeout(() => {
            LocalStorageAccessor.clearCredentialInfo()
          }, 60000)
        }

        return
      }

      let injectCode = ''
      if (sliderLink.link === process.env.VUE_APP_SFBINGO_URL) {
        const credentialInfo = {
          organizationId: UserStore.value.user.value._organization,
          userId: UserStore.value.user.value.id,
          accessToken: LoginStore.value.accessToken,
          refreshToken: LoginStore.value.refreshToken,
        }
        const sCredentialInfo = JSON.stringify(credentialInfo)
        injectCode = `window.onLoadCredentials('${sCredentialInfo}');`
      }

      // InAppBrowserでサイトを開く
      inAppBrowser.open(
        sliderLink.link,
        '_blank',
        undefined,
        async (result: InAppBrowserResult) => {
          if (result?.isNetworkError) {
            await MessageDialogStore.value.open({
              title: I18n.t('apiNotification.dialog.0.title') as string,
              message: I18n.t('apiNotification.dialog.0.message') as string,
            })
          } else if (result?.isError) {
            await MessageDialogStore.value.open({
              title: I18n.t('HomePage.errors.headlineLinkError.title') as string,
              message: I18n.t('HomePage.errors.headlineLinkError.message') as string,
            })
          }
        },
        false,
        injectCode,
      )
    }

    /**
     * 画像読み込み失敗時に画像を非表示にする
     */
    const onImageError = (event: Event) => {
      const target = event.target as HTMLImageElement
      if (target) {
        target.style.opacity = '0'
      }
    }

    return {
      isCircuitMode: appConfigStore.currentCircuitMode,
      thumbnailPath,
      onClickAnchorLink,
      onImageError,
    }
  },
})
</script>

<style lang="scss" scoped>
.img-slide-parts {
  display: flex;
  justify-content: center;
  align-items: center;

  &__slide {
    position: relative;
    width: 100%;
    height: 100%;
    background-color: $black;

    &::after {
      position: absolute;
      display: block;
      content: '';
      width: 100%;
      left: 0;
      bottom: 0;
      aspect-ratio: 375 / 144;
      background: linear-gradient(180deg, rgba(#000, 0) 0%, #000 100%);
      z-index: 2;

      @media screen and (min-width: $responsiveTabletWidth - 1px) {
        aspect-ratio: 800 / 219;
      }
    }
  }

  &__img {
    width: 100%;
    aspect-ratio: 375 / 210;
    object-fit: contain;
    object-position: top center;

    @media screen and (min-width: $responsiveTabletWidth - 1px) {
      aspect-ratio: 800 / 300;
    }
  }

  &__content {
    position: absolute;
    bottom: 0;
    right: 50%;
    width: min(100%, 800px);
    padding: 0 min(#{convertPXtoVW(12)}, 12px);
    z-index: 3;
    color: #fff;
    transform: translate(50%, 0);
  }

  &__text {
    display: -webkit-box;
    width: 100%;
    font-weight: 600;
    font-size: min(#{convertPXtoVW(14), 14px});
    line-height: 1.43;
    text-align: center;
    letter-spacing: 0.02em;
    font-feature-settings: normal;
    overflow: hidden;
    overflow-wrap: break-word;

    &-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: calc(67 / 375) * 100vw;

      @media screen and (min-width: $responsiveTabletWidth - 1px) {
        min-height: min(#{convertPXtoVW(44), 44px});
        padding-top: min(#{convertPXtoVW(12), 12px});
        padding-bottom: min(#{convertPXtoVW(12), 12px});
      }
    }

    i {
      display: inline-block;
      height: min(#{convertPXtoVW(18), 18px});
    }

    img {
      display: inline-block;
      width: min(#{convertPXtoVW(18), 18px});
      height: min(#{convertPXtoVW(18), 18px});
      vertical-align: middle;
      transform: translateY(-0.1em);
    }

    .img-slide-parts__router & {
      -webkit-line-clamp: 2;
    }

    .img-slide-parts__link & {
      -webkit-line-clamp: 3;
    }

    /* autoprefixer: off */
    -webkit-box-orient: vertical;

    /*
      以下のエラーがでるため、ブロックを分けて記載する。
      `Second Autoprefixer control comment was ignored. Autoprefixer applies control comment to whole block, not to next rules.`
    */
  }

  /* autoprefixer: on */

  &__button {
    display: inline-flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: center;
    width: auto;
    height: 2.286em;
    padding: 0 1.14em;
    gap: 4px;
    border-radius: 2em;
    @include fontButton(12);

    color: $black;
    background: $white;
    border: 1px solid rgba(0, 0, 0, 20%);
    cursor: pointer;

    &--circuit-mode {
      color: $white;
      background: linear-gradient(
        90deg,
        rgba(125, 46, 163, 0.85) 0%,
        rgba(202, 140, 232, 0.85) 72.52%,
        rgba(125, 46, 163, 0.85) 107.1%
      );

      svg path {
        fill: $white;
      }
    }

    &-wrapper {
      padding-bottom: min(#{convertPXtoVW(16)}, 16px);
      margin-top: 12px;
    }
  }
}
</style>
